import { Component, OnInit, ViewEncapsulation, AfterViewInit, OnDestroy } from '@angular/core';
import { InsightsService } from './insights.service';
import { Router } from '@angular/router';
import { HeaderService } from '../header/header.service';
import { AppSettings } from '../../config/config';
import { TranslateService } from '@ngx-translate/core';
import { Chart, registerables } from 'chart.js';
import { firstValueFrom } from "rxjs";
Chart.register(...registerables);
declare var $: any;

@Component({
  selector: 'app-insights',
  templateUrl: './insights.component.html',
  styleUrls: [
    './../../assets/css/theme-default/bootstrap.css',
    './../../assets/css/theme-default/materialadmin.css',
    './../../assets/css/theme-default/font-awesome.min.css',
    './../../assets/css/theme-default/material-design-iconic-font.min.css',
    './insights.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [InsightsService, HeaderService]
})
export class InsightsComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    private insightsService: InsightsService,
    private router: Router,
    private headerService: HeaderService,
    private translate: TranslateService
  ) {
  }

  getAuthToken: any;
  getSchoolId: any;
  getAcademicId: any;
  preLoader = true;
  schoolName: string = '';
  dataGeneratedAt: string = '';
  private charts: any[] = [];

  // Overall Metrics
  overallHomeworkCompletionRate: number = 0;
  overallAttendanceRate: number = 0;
  highEngagementStudentRatio: number = 0;
  lowEngagementStudentRatio: number = 0;

  // Insight Metadata (for tooltips and info)
  insightMetadata: any = {};

  // Class Performance - Arrays to support multiple classes
  mostRegularClass: any[] = [];
  mostIrregularClass: any[] = [];
  classWithBestHomeworkDiscipline: any[] = [];
  classWithHomeworkEngagementRisk: any[] = [];

  // Subject Performance
  topPerformingSubject: any[] = [];
  mostChallengingSubject: any[] = [];

  // Teacher Performance
  mostEngagingTeacher: any[] = [];
  teacherSupportRequired: any[] = [];

  // Attendance Patterns
  subjectAttendanceSensitivity: any[] = [];
  highRiskSubjects: any[] = [];
  bestAttendanceDay: string = '';
  worstAttendanceDay: string = '';
  worstAttendanceDays: any[] = [];

  // Homework Patterns
  homeworkBurnoutRiskDay: string = '';
  homeworkBurnoutRiskData: any = null;
  peakHomeworkDaysPerSubject: any[] = [];

  // Chart Data
  weeklyAttendanceData: number[] = [];
  weeklyAttendanceLabels: string[] = [];
  subjectPerformanceData: any[] = [];
  homeworkDistributionData: number[] = [];
  homeworkDistributionLabels: string[] = [];

  ngOnInit() {
    this.preLoader = true;
    
    const tokenCookie = document.cookie.split(';').find(cookie => cookie.trim().startsWith('usertoken='));
    let tokenValue = tokenCookie ? tokenCookie.split('=')[1] : null;
    this.getAuthToken = tokenValue;
    
    const getSchoolIdDetails = document.cookie.split(';').find(cookie => cookie.trim().startsWith('schoolinfo='));
    let getSchoolIdDetailsValue = getSchoolIdDetails ? getSchoolIdDetails.split('=')[1] : null;
    this.getSchoolId = getSchoolIdDetailsValue;

    if (this.getAuthToken && this.getSchoolId) {
      this.initAcademicIdThenLoadInsights();
    } else {
      this.preLoader = false;
      this.router.navigate(['/login']);
    }
  }

  /* localStorage's schoolAllInfo (and its currentAcademicId) is only written
     once HeaderV2Component's own async school-info call resolves — a chain
     that races this component's ngOnInit and can still be empty right after
     login. Rather than read that possibly-stale cache, fetch the same
     school-info endpoint directly here and take currentAcademicId straight
     from the response, so Insights loads correctly on first visit instead
     of needing a page refresh. */
  async initAcademicIdThenLoadInsights() {
    try {
      const response: any = await firstValueFrom(this.headerService.schoolInfo(this.getSchoolId, this.getAuthToken));
      this.schoolName = response?.schoolName;
      this.getAcademicId = response?.currentAcademicId || 0;
    } catch (error) {
      console.error('Error fetching school info:', error);
      this.getAcademicId = 0;
    }
    this.loadInsightsData();
  }

  
  async loadInsightsData() {
  const lang = 'en'; 

  try {
    const response: any = await firstValueFrom(
      this.insightsService.fetchInsightsData(
        this.getAcademicId,
        lang,
        this.getAuthToken
      )
    );

    if (response && response.status && response.body && response.body.insights) {
      const insights = response.body.insights;
      this.parseInsightsData(insights);
console.log("response",response);

      // Use lastDataGenerated from API response, fallback to current date
      this.dataGeneratedAt = response.body.lastDataGenerated

      this.preLoader = false;

      setTimeout(() => {
        this.initializeCharts();
      }, 100);
    } else {
      console.error('Invalid response format:', response);
      this.preLoader = false;
    }
  } catch (error) {
    console.error('Error loading insights:', error);
    this.preLoader = false;
  }
}


  parseInsightsData(insights: any) {
    // Homework Completion
    if (insights.homework_completion?.insightData?.[0]) {
      const data = insights.homework_completion.insightData[0];
      // overallCompletionRate is already a percentage (0.96 = 0.96%)
      this.overallHomeworkCompletionRate = data.overallCompletionRate || 0;
      this.insightMetadata['homework_completion'] = insights.homework_completion.insightSlugInfo;
    }

    // Attendance Rate
    if (insights.attendance_rate?.insightData?.[0]) {
      const data = insights.attendance_rate.insightData[0];
      // overallAttendanceRate is already a percentage, not a decimal
      this.overallAttendanceRate = data.overallAttendanceRate || 0;
      this.insightMetadata['attendance_rate'] = insights.attendance_rate.insightSlugInfo;
    }

    // High Engagement
    if (insights.high_engagement?.insightData?.[0]) {
      const data = insights.high_engagement.insightData[0];
      // overallHighEngagementStudentRatio is already a percentage (0.33 = 0.33%)
      this.highEngagementStudentRatio = data.overallHighEngagementStudentRatio || 0;
      this.insightMetadata['high_engagement'] = insights.high_engagement.insightSlugInfo;
    }

    // Low Engagement
    if (insights.low_engagement?.insightData?.[0]) {
      const data = insights.low_engagement.insightData[0];
      // overallLowEngagementStudentRatio is already a percentage
      this.lowEngagementStudentRatio = data.overallLowEngagementStudentRatio || 0;
      this.insightMetadata['low_engagement'] = insights.low_engagement.insightSlugInfo;
    }

    // Most Regular Class
    if (insights.most_regular_class?.insightData) {
      this.mostRegularClass = insights.most_regular_class.insightData.map((item: any) => ({
        name: `${item.classNumber}${item.classDivision ? '-' + item.classDivision : ''}`,
        attendance: item.avgAbsentPercentage ? (100 - item.avgAbsentPercentage) : 0,
        classId: item.classId,
        classNumber: item.classNumber,
        classDivision: item.classDivision,
        totalStudents: item.totalStudents,
        avgAbsentPercentage: item.avgAbsentPercentage
      }));
      this.insightMetadata['most_regular_class'] = insights.most_regular_class.insightSlugInfo;
    }

    // Most Irregular Class
    if (insights.most_irregular_class?.insightData) {
      this.mostIrregularClass = insights.most_irregular_class.insightData.map((item: any) => ({
        name: `${item.classNumber}${item.classDivision ? '-' + item.classDivision : ''}`,
        attendance: item.avgAbsentPercentage ? (100 - item.avgAbsentPercentage) : 0,
        classId: item.classId,
        classNumber: item.classNumber,
        classDivision: item.classDivision,
        totalStudents: item.totalStudents,
        avgAbsentPercentage: item.avgAbsentPercentage
      }));
      this.insightMetadata['most_irregular_class'] = insights.most_irregular_class.insightSlugInfo;
    }

    // Best Homework Discipline
    if (insights.best_homework_discipline?.insightData) {
      this.classWithBestHomeworkDiscipline = insights.best_homework_discipline.insightData.map((item: any) => ({
        name: item.className || 'N/A',
        completion: item.studentsPercentageAbove90PercentCompleteness || 0, // Percentage of students with >90% completion
        classId: item.classId,
        above90: item.studentsPercentageAbove90PercentCompleteness,
        between50to90: item.studentsPercentageBetween50To90PercentCompleteness,
        below50: item.studentsPercentageBelow50PercentCompleteness
      }));
      this.insightMetadata['best_homework_discipline'] = insights.best_homework_discipline.insightSlugInfo;
    }

    // Homework Engagement Risk
    if (insights.homework_engagement_risk?.insightData) {
      this.classWithHomeworkEngagementRisk = insights.homework_engagement_risk.insightData.map((item: any) => ({
        name: item.className || 'N/A',
        completion: item.studentsPercentageBelow50PercentCompleteness || 0, // Percentage of students with <50% completion
        classId: item.classId,
        above90: item.studentsPercentageAbove90PercentCompleteness,
        between50to90: item.studentsPercentageBetween50To90PercentCompleteness,
        below50: item.studentsPercentageBelow50PercentCompleteness
      }));
      this.insightMetadata['homework_engagement_risk'] = insights.homework_engagement_risk.insightSlugInfo;
    }

    // Top Performing Subject
    if (insights.top_performing_subject?.insightData) {
      this.topPerformingSubject = insights.top_performing_subject.insightData.map((item: any) => ({
        name: item.subjectName,
        engagement: item.overallCompletionPercent || 0, // Already a percentage
        subjectId: item.subjectId,
        totalHomeworksAssigned: item.totalHomeworksAssigned,
        totalExpectedSubmissions: item.totalExpectedSubmissions,
        totalActualSubmissions: item.totalActualSubmissions,
        overallCompletionPercent: item.overallCompletionPercent,
        avgDiaryCompletionPercent: item.avgDiaryCompletionPercent
      }));
      this.insightMetadata['top_performing_subject'] = insights.top_performing_subject.insightSlugInfo;
    }

    // Most Challenging Subject
    if (insights.most_challenging_subject?.insightData) {
      this.mostChallengingSubject = insights.most_challenging_subject.insightData.map((item: any) => ({
        name: item.subjectName,
        engagement: item.overallCompletionPercent || 0, // Already a percentage
        subjectId: item.subjectId,
        totalHomeworksAssigned: item.totalHomeworksAssigned,
        totalExpectedSubmissions: item.totalExpectedSubmissions,
        totalActualSubmissions: item.totalActualSubmissions,
        overallCompletionPercent: item.overallCompletionPercent,
        avgDiaryCompletionPercent: item.avgDiaryCompletionPercent
      }));
      this.insightMetadata['most_challenging_subject'] = insights.most_challenging_subject.insightSlugInfo;
    }

    // Most Engaging Teacher
    if (insights.most_engaging_teacher?.insightData) {
      this.mostEngagingTeacher = insights.most_engaging_teacher.insightData.map((item: any) => ({
        name: item.staffName,
        completion: item.overallCompletionPercent || 0, // Already a percentage
        staffId: item.staffId,
        totalHomeworksAssigned: item.totalHomeworksAssigned,
        totalExpectedSubmissions: item.totalExpectedSubmissions,
        totalActualSubmissions: item.totalActualSubmissions,
        overallCompletionPercent: item.overallCompletionPercent,
        avgDiaryCompletionPercent: item.avgDiaryCompletionPercent
      }));
      this.insightMetadata['most_engaging_teacher'] = insights.most_engaging_teacher.insightSlugInfo;
    }

    // Teacher Support Required
    if (insights.teacher_support_required?.insightData) {
      this.teacherSupportRequired = insights.teacher_support_required.insightData.map((item: any) => ({
        name: item.staffName,
        completion: item.overallCompletionPercent || 0, // Already a percentage
        staffId: item.staffId,
        totalHomeworksAssigned: item.totalHomeworksAssigned,
        totalExpectedSubmissions: item.totalExpectedSubmissions,
        totalActualSubmissions: item.totalActualSubmissions,
        overallCompletionPercent: item.overallCompletionPercent,
        avgDiaryCompletionPercent: item.avgDiaryCompletionPercent
      }));
      this.insightMetadata['teacher_support_required'] = insights.teacher_support_required.insightSlugInfo;
    }

    // Initialize weekly attendance chart data with translations
    this.weeklyAttendanceLabels = [
      this.translate.instant('aiInsights.monday'),
      this.translate.instant('aiInsights.tuesday'),
      this.translate.instant('aiInsights.wednesday'),
      this.translate.instant('aiInsights.thursday'),
      this.translate.instant('aiInsights.friday'),
      this.translate.instant('aiInsights.saturday'),
      this.translate.instant('aiInsights.sunday')
    ];
    this.weeklyAttendanceData = new Array(7).fill(null); // Use null to indicate no data
    const dayIndexMap: any = { 'Monday': 0, 'Tuesday': 1, 'Wednesday': 2, 'Thursday': 3, 'Friday': 4, 'Saturday': 5, 'Sunday': 6 };

    // Best Attendance Day
    if (insights.best_attendance_day?.insightData?.[0]) {
      const data = insights.best_attendance_day.insightData[0];
      this.bestAttendanceDay = data.dayName || '';
      this.insightMetadata['best_attendance_day'] = insights.best_attendance_day.insightSlugInfo;
      
      // Add best day data to chart
      if (dayIndexMap[data.dayName] !== undefined) {
        this.weeklyAttendanceData[dayIndexMap[data.dayName]] = data.avgAttendancePercentage || 0;
      }
    }

    // Worst Attendance Day
    if (insights.worst_attendance_day?.insightData) {
      this.worstAttendanceDays = insights.worst_attendance_day.insightData;
      if (this.worstAttendanceDays.length > 0) {
        this.worstAttendanceDay = this.worstAttendanceDays[0].dayName || '';
      }
      this.insightMetadata['worst_attendance_day'] = insights.worst_attendance_day.insightSlugInfo;
      
      // Add worst days data to chart (only if not already set by best day)
      this.worstAttendanceDays.forEach((day: any) => {
        if (dayIndexMap[day.dayName] !== undefined && this.weeklyAttendanceData[dayIndexMap[day.dayName]] === null) {
          this.weeklyAttendanceData[dayIndexMap[day.dayName]] = day.avgAttendancePercentage || 0;
        }
      });
    }

    // Replace null values with 0 for chart display
    this.weeklyAttendanceData = this.weeklyAttendanceData.map((val: any) => val === null ? 0 : val);

    // Subject Attendance Sensitivity (for chart)
    if (insights.subject_attendance_sensitivity?.insightData) {
      this.subjectAttendanceSensitivity = insights.subject_attendance_sensitivity.insightData;
      this.insightMetadata['subject_attendance_sensitivity'] = insights.subject_attendance_sensitivity.insightSlugInfo;
      
      // Prepare subject performance chart data
      this.subjectPerformanceData = insights.subject_attendance_sensitivity.insightData.map((item: any) => ({
        name: item.subjectName,
        value: item.avgAttendancePercentage || 0,
        subjectId: item.subjectId
      })).sort((a: any, b: any) => b.value - a.value).slice(0, 5); // Top 5 subjects
    }

    // Homework Burnout Risk Day
    if (insights.homework_burnout_risk_day?.insightData?.[0]) {
      const data = insights.homework_burnout_risk_day.insightData[0];
      this.homeworkBurnoutRiskDay = data.peakHomeworkDay || '';
      this.homeworkBurnoutRiskData = data;
      this.insightMetadata['homework_burnout_risk_day'] = insights.homework_burnout_risk_day.insightSlugInfo;
      
      // Prepare homework distribution chart data - use homeworkCountOnPeakDay
      this.homeworkDistributionLabels = [
        this.translate.instant('aiInsights.monday'),
        this.translate.instant('aiInsights.tuesday'),
        this.translate.instant('aiInsights.wednesday'),
        this.translate.instant('aiInsights.thursday'),
        this.translate.instant('aiInsights.friday'),
        this.translate.instant('aiInsights.saturday'),
        this.translate.instant('aiInsights.sunday')
      ];
      this.homeworkDistributionData = new Array(7).fill(0);
      const dayIndexMap: any = { 'Monday': 0, 'Tuesday': 1, 'Wednesday': 2, 'Thursday': 3, 'Friday': 4, 'Saturday': 5, 'Sunday': 6 };
      if (dayIndexMap[data.peakHomeworkDay] !== undefined) {
        this.homeworkDistributionData[dayIndexMap[data.peakHomeworkDay]] = data.homeworkCountOnPeakDay || 0;
      }
    }

    // High Risk Subjects
    if (insights.high_risk_subject?.insightData) {
      this.highRiskSubjects = insights.high_risk_subject.insightData.map((item: any) => ({
        name: item.subjectName,
        attendance: item.avgAttendancePercentage || 0,
        homework: item.overallCompletionPercent || 0,
        subjectId: item.subjectId,
        classId: item.classId,
        classNumber: item.classNumber || '',
        classDivision: item.classDivision || '',
        avgAttendancePercentage: item.avgAttendancePercentage,
        overallCompletionPercent: item.overallCompletionPercent,
        totalHomeworksAssigned: item.totalHomeworksAssigned,
        totalExpectedSubmissions: item.totalExpectedSubmissions,
        totalActualSubmissions: item.totalActualSubmissions
      }));
      this.insightMetadata['high_risk_subject'] = insights.high_risk_subject.insightSlugInfo;
    }
  }

  ngAfterViewInit() {
    // Charts will be initialized after data loads
  }

  ngOnDestroy() {
    // Destroy all charts
    this.charts.forEach(chart => {
      if (chart) {
        chart.destroy();
      }
    });
    this.charts = [];
    
    // Remove any existing tooltip
    const tooltip = document.getElementById('dynamic-tooltip');
    if (tooltip) {
      tooltip.remove();
    }
  }

  initializeCharts() {
    this.createSubjectPerformanceChart();
  }

  createSubjectPerformanceChart() {
    const ctx = document.getElementById('subjectPerformanceChart') as HTMLCanvasElement;
    if (!ctx) return;

    // Use actual data from API or fallback to empty
    const chartData = this.subjectPerformanceData.length > 0
      ? this.subjectPerformanceData
      : [];
    const data = chartData.map((item: any) => item.value || 0);
    const labels = chartData.map((item: any) => item.name || 'N/A');
    
    // Create gradients based on actual data length
    const gradients = labels.map((_, index) => {
      const gradient = ctx.getContext('2d')?.createLinearGradient(0, 0, 300, 0);
      if (gradient) {
        const colors = [
          ['#667eea', '#764ba2'], // Purple
          ['#f56565', '#e53e3e'], // Red
          ['#f6ad55', '#ed8936'], // Orange
          ['#4299e1', '#3182ce'], // Blue
          ['#9f7aea', '#805ad5']  // Purple
        ];
        const colorIndex = index % colors.length;
        gradient.addColorStop(0, colors[colorIndex][0]);
        gradient.addColorStop(1, colors[colorIndex][1]);
      }
      return gradient;
    });

    const chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Performance %',
          data: data,
          backgroundColor: gradients.map((g, i) => g || '#667eea'),
          borderRadius: {
            topRight: 8,
            bottomRight: 8,
            topLeft: 0,
            bottomLeft: 0
          },
          borderSkipped: false,
          barThickness: 4,
          maxBarThickness: 5
        }]
      },
      options: {
        indexAxis: 'y', // Horizontal bars
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 0,
            right: 10,
            top: 10,
            bottom: 10
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            padding: 12,
            titleFont: { size: 12, weight: 'bold' },
            bodyFont: { size: 11 },
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
              label: function(context) {
                return 'Performance: ' + context.parsed.x + '%';
              }
            }
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            max: 100,
            grid: {
              display: true,
              color: '#e2e8f0',
              lineWidth: 1
            },
            border: {
              display: false
            },
            ticks: {
              font: { size: 10 },
              color: '#a0aec0',
              stepSize: 20,
              callback: function(value) {
                return value + '%';
              }
            }
          },
          y: {
            grid: {
              display: false
            },
            border: {
              display: false
            },
            ticks: {
              font: { size: 12, weight: 'bold' },
              color: '#4a5568',
              padding: 16
            }
          }
        },
        animation: {
          duration: 1000,
          easing: 'easeOutQuart'
        }
      }
    });
    this.charts.push(chart);
  }


  showTooltip(event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    const tooltipText = target.getAttribute('data-tooltip');
    if (!tooltipText) return;

    // Remove existing tooltip if any
    const existingTooltip = document.getElementById('dynamic-tooltip');
    if (existingTooltip) {
      existingTooltip.remove();
    }

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.id = 'dynamic-tooltip';
    tooltip.className = 'dynamic-tooltip';
    tooltip.textContent = tooltipText;
    document.body.appendChild(tooltip);

    // Position tooltip
    const rect = target.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const top = rect.top - tooltipRect.height - 8;
    const left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);

    tooltip.style.top = `${Math.max(10, top)}px`;
    tooltip.style.left = `${Math.max(10, Math.min(left, window.innerWidth - tooltipRect.width - 10))}px`;
  }

  hideTooltip(event: MouseEvent) {
    const tooltip = document.getElementById('dynamic-tooltip');
    if (tooltip) {
      tooltip.remove();
    }
  }

  showClassesTooltip(event: MouseEvent, items: any[], metricType: string) {
    const target = event.currentTarget as HTMLElement;
    
    // Remove existing tooltip if any
    const existingTooltip = document.getElementById('dynamic-classes-tooltip');
    if (existingTooltip) {
      existingTooltip.remove();
    }

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.id = 'dynamic-classes-tooltip';
    tooltip.className = 'dynamic-classes-tooltip';
    
    // Determine header text based on metric type
    let headerText = 'All Items:';
    if (metricType === 'attendance' || metricType === 'completion') {
      headerText = 'All Classes:';
    } else if (metricType === 'engagement') {
      headerText = 'All Subjects:';
    }
    
    // Create header
    const header = document.createElement('div');
    header.className = 'tooltip-header';
    header.textContent = headerText;
    tooltip.appendChild(header);

    // Create items
    items.forEach((item: any) => {
      const itemDiv = document.createElement('div');
      itemDiv.className = 'tooltip-class-item';
      
      const name = document.createElement('span');
      name.className = 'tooltip-class-name';
      name.textContent = item.name;
      
      const metric = document.createElement('span');
      metric.className = 'tooltip-class-metric';
      if (metricType === 'attendance') {
        metric.textContent = item.attendance ? (item.attendance.toFixed(2) + '%') : '0%';
      } else if (metricType === 'engagement') {
        metric.textContent = item.engagement ? (item.engagement.toFixed(2) + '%') : '0%';
      } else {
        metric.textContent = item.completion ? (item.completion.toFixed(2) + '%') : '0%';
      }
      
      itemDiv.appendChild(name);
      itemDiv.appendChild(metric);
      tooltip.appendChild(itemDiv);
    });

    document.body.appendChild(tooltip);

    // Position tooltip above the element
    const rect = target.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const top = rect.top - tooltipRect.height - 8;
    const left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);

    tooltip.style.top = `${Math.max(10, top)}px`;
    tooltip.style.left = `${Math.max(10, Math.min(left, window.innerWidth - tooltipRect.width - 10))}px`;
  }

  hideClassesTooltip(event: MouseEvent) {
    const tooltip = document.getElementById('dynamic-classes-tooltip');
    if (tooltip) {
      tooltip.remove();
    }
  }
}
